import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-f9dfdd86"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "salary-content" }
const _hoisted_2 = {
  key: 0,
  class: "salary"
}
const _hoisted_3 = { class: "title" }
const _hoisted_4 = { class: "text-size-14 text-weight-500" }
const _hoisted_5 = { class: "text-gray-05 text-size-12" }
const _hoisted_6 = { class: "detail" }
const _hoisted_7 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("p", { class: "text-mild text-size-14 text-weight-500 px-3" }, "\n          内訳\n        ", -1))

import Header from "@/components/organisms/common/Header.vue";
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import { useRoute, useRouter } from "vue-router";
import { computed, onBeforeMount, ref } from "vue";
import { useLoader } from "@/utils/loader";
import { useErrorModal } from "@/utils/modal";
import { salaryService } from "@/app/services/SalaryService";
import {
  BalanceHistory,
  CorporationSalarySummary,
  SalarySummary
} from "@/types/balance/BalanceHistory";
import {
  formatFromISOString,
  formatFromUnixTimestamp,
  parseISO,
  subMonths
} from "@/utils/date";
import SalaryCard from "@/components/organisms/mypage/SalaryCard.vue";
import SalaryDetailList from "@/components/organisms/mypage/SalaryDetailList.vue";
import SalaryDetailModal from "@/components/organisms/mypage/SalaryDetailModal.vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'Detail',
  setup(__props) {

const errorModal = useErrorModal();
const route = useRoute();
const router = useRouter();
const loader = useLoader();

const history = ref<BalanceHistory | null>(null);
const rewardHistories = ref<BalanceHistory[]>([]);
const selectedCorporationSalarySummary = ref<CorporationSalarySummary | null>(
  null
);
const isShowDetailModal = ref<boolean>(false);

onBeforeMount(async () => {
  try {
    loader.show();
    const transferRequestId = Number(route.query.transferRequestId as string);
    await Promise.all([
      await fetchBalanceHistory(transferRequestId),
      await fetchBalanceHistoriesForRelatedReward(transferRequestId)
    ]);
  } catch (e) {
    await errorModal.showErrorModal(e as Error);
  } finally {
    loader.hide();
  }
});

const transactionDate = computed(() => {
  if (!history.value) return "";
  return formatFromISOString(history.value.createdAt, "yy/MM/dd");
});

const transactionText = computed(() => {
  if (!history.value) return "";
  switch (history.value.transactionType) {
    case "withdrawal":
      return "早期引出";
    case "salary":
      return `定期給与自動引出（${formatFromUnixTimestamp(
        subMonths(parseISO(history.value.createdAt), 1),
        "MM"
      )}月勤務分）`;
    default:
      return "";
  }
});

const statusLabelVariant = computed(() => {
  if (!history.value?.transferRequest) return "";
  switch (history.value.transferRequest.status) {
    case "requested":
      return "warning";
    case "done":
      return "info";
    case "error":
      return "danger";
    default:
      throw new Error("invalid status");
  }
});

const statusLabel = computed(() => {
  if (!history.value?.transferRequest) return "";
  switch (history.value.transferRequest.status) {
    case "requested":
      return "処理中";
    case "done":
      return "処理済";
    case "error":
      return history.value.transferRequest.errorMessage || "不明なエラー";
    default:
      throw new Error("invalid status");
  }
});

const salarySummary = computed(() => {
  const summary = rewardHistories.value.reduce<SalarySummary>(
    (acc, relatedHistory) => {
      acc.reward += salaryService.getReward(relatedHistory);
      acc.optionValue += relatedHistory.optionValue;
      acc.withholdingTax += relatedHistory.withholdingTax;
      acc.totalIncome += salaryService.getTotalIncome(relatedHistory);
      return acc;
    },
    {
      reward: 0,
      optionValue: 0,
      withholdingTax: 0,
      commission: 0,
      totalIncome: 0
    }
  );
  summary.commission = history.value?.optionValue || 0;
  summary.totalIncome -= summary.commission;
  return summary;
});

const corporationSalarySummaries = computed(() => {
  return rewardHistories.value.reduce<CorporationSalarySummary[]>(
    (acc, relatedHistory) => {
      if (!relatedHistory.order) {
        throw new Error("お仕事に紐づいていない明細があるため表示できません");
      }
      const reward = salaryService.getReward(relatedHistory);
      const totalIncome = salaryService.getTotalIncome(relatedHistory);
      const corporation = acc.find(
        c => c.corporationId === relatedHistory.order!.office.corporation.id
      );
      if (!corporation) {
        acc.push({
          corporationId: relatedHistory.order.office.corporation.id,
          name: relatedHistory.order.office.corporation.name,
          totalIncome,
          officeSalarySummaries: [
            {
              officeId: relatedHistory.order.office.id,
              name: relatedHistory.order.office.name,
              reward,
              optionValue: relatedHistory.optionValue,
              withholdingTax: relatedHistory.withholdingTax,
              totalIncome
            }
          ]
        });
        return acc;
      }
      const office = corporation.officeSalarySummaries.find(
        o => o.officeId === relatedHistory.order!.office.id
      );
      if (!office) {
        corporation.officeSalarySummaries.push({
          officeId: relatedHistory.order.office.id,
          name: relatedHistory.order.office.name,
          reward,
          optionValue: relatedHistory.optionValue,
          withholdingTax: relatedHistory.withholdingTax,
          totalIncome
        });
      } else {
        office.reward += reward;
        office.optionValue += relatedHistory.optionValue;
        office.withholdingTax += relatedHistory.withholdingTax;
        office.totalIncome += totalIncome;
      }
      corporation.totalIncome += totalIncome;
      return acc;
    },
    []
  );
});

function onClickBack() {
  router.back();
}

async function fetchBalanceHistory(transferRequestId: number) {
  const response = await salaryService.fetchBalanceHistories({
    transfer_request_id: transferRequestId
  });
  if (response.results.length === 0) {
    throw new Error("給与内訳が見つかりませんでした");
  }
  history.value = response.results[0];
}

async function fetchBalanceHistoriesForRelatedReward(
  transferRequestId: number
) {
  const response = await salaryService.fetchBalanceHistories({
    transfer_request_related_with_reward_id: transferRequestId,
    ordering: "-created_at"
  });
  rewardHistories.value = response.results;
}

function onClickDetail(corporationSalarySummary: CorporationSalarySummary) {
  selectedCorporationSalarySummary.value = corporationSalarySummary;
  isShowDetailModal.value = true;
}

function onCloseDetailModal() {
  isShowDetailModal.value = false;
  selectedCorporationSalarySummary.value = null;
}

return (_ctx: any,_cache: any) => {
  const _component_BBadge = _resolveComponent("BBadge")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(Header, null, {
      left: _withCtx(() => [
        _createVNode(FeatherIcon, {
          icon: "chevron-left",
          onClick: onClickBack
        })
      ]),
      default: _withCtx(() => [
        _createTextVNode("\n      給与内訳\n    ")
      ], undefined, true),
      _: 1
    }),
    _createTextVNode(),
    _createElementVNode("div", _hoisted_1, [
      (history.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
            _createElementVNode("div", _hoisted_3, [
              _createElementVNode("p", _hoisted_4, _toDisplayString(transactionDate.value), 1),
              _createTextVNode(),
              _createElementVNode("p", _hoisted_5, _toDisplayString(transactionText.value), 1),
              _createTextVNode(),
              _createVNode(_component_BBadge, {
                pill: "",
                variant: statusLabelVariant.value,
                class: "status-badge"
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(statusLabel.value), 1)
                ], undefined, true),
                _: 1
              }, 8, ["variant"])
            ]),
            _createTextVNode(),
            _createVNode(SalaryCard, { salarySummary: salarySummary.value }, null, 8, ["salarySummary"])
          ]))
        : _createCommentVNode("", true),
      _createTextVNode(),
      _createElementVNode("div", _hoisted_6, [
        _hoisted_7,
        _createTextVNode(),
        _createVNode(SalaryDetailList, {
          corporationSalarySummaries: corporationSalarySummaries.value,
          onClickDetail: onClickDetail,
          class: "mt-2"
        }, null, 8, ["corporationSalarySummaries"])
      ])
    ]),
    _createTextVNode(),
    (selectedCorporationSalarySummary.value)
      ? (_openBlock(), _createBlock(SalaryDetailModal, {
          key: 0,
          isShowDetailModal: isShowDetailModal.value,
          corporationSalarySummary: selectedCorporationSalarySummary.value,
          onClose: onCloseDetailModal
        }, null, 8, ["isShowDetailModal", "corporationSalarySummary"]))
      : _createCommentVNode("", true)
  ]))
}
}

})